import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import React, { useId } from 'react';
import { useTranslation } from 'react-i18next';
import { useModalDialog } from 'reusable/useModalDialog';

interface DiscardChangesDialogProps {
  isOpen: boolean;
  onCancel: () => void;
  onDiscard: () => void;
}

const DiscardChangesDialog: React.FC<DiscardChangesDialogProps> = ({
  isOpen,
  onCancel,
  onDiscard,
}) => {
  const { t } = useTranslation('client');
  // ADM-16。Escape で閉じる・フォーカスを中に留める・閉じたら開いた操作へ戻す。
  // RV-125。見出しの id は文書の中で一意にする（重なったダイアログが別の見出しで読まれた）。
  const headingId = useId();
  const dialogRef = useModalDialog(isOpen, onCancel);

  if (!isOpen) return null;

  return (
    <div className="fixed z-10 inset-0 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 py-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 transition-opacity" aria-hidden="true">
          <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
        </div>
        <span
          className="hidden sm:inline-block sm:align-middle sm:h-screen"
          aria-hidden="true"
        >
          &#8203;
        </span>
        <div
          className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full"
          role="dialog"
          aria-modal="true"
          aria-labelledby={headingId}
          ref={dialogRef}
          tabIndex={-1}
        >
          <div className="bg-white">
            <div className="sm:flex sm:items-start">
              <div className="mt-3 text-left sm:mt-0 w-full">
                <div className="flex justify-between items-center mt-4 mx-6">
                  <h3
                    className="text-subtitle-1 font-medium text-neutral/500"
                    id={headingId}
                  >
                    {t('discard.title')}
                  </h3>
                  <button type="button"
                    onClick={onCancel}
                    className="text-gray-400 hover:text-gray-500 focus:outline-none"
                  >
                    <span className="sr-only">{t('ui.close', { ns: 'common' })}</span>
                    <svg
                      className="h-6 w-6"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      aria-hidden="true"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
                </div>
                <hr className="border-neutral/50 mt-4 w-full" />
                <div className="flex flex-col p-6 gap-6 items-center">
                  <ExclamationTriangleIcon className="bg-[#FFB702] rounded-full text-white h-24 w-24 p-4" />

                  <p className="text-center whitespace-nowrap text-body-2 text-neutral/500">
                    {t('discard.subtitle_1')}
                    <br />
                    {t('discard.subtitle_2')}
                  </p>
                </div>
                <div className="px-4 py-3 border-t-[1px] grid grid-cols-2 gap-4">
                  <button
                    type="button"
                    className="w-full inline-flex justify-center items-center rounded-md border bg-primary/50 text-subtitle-2 text-primary/300"
                    onClick={onCancel}
                  >
                    {t('action.cancel')}
                  </button>
                  <button type="button"
                    onClick={onDiscard}
                    className="w-full inline-flex justify-center items-center rounded-md bg-primary/300 text-subtitle-2 text-white h-12"
                  >
                    {t('action.discard')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DiscardChangesDialog;
